import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth, useLiveUser, useSession } from "./contexts/AuthContext";
import { SessionAccountInformation } from "./firebase/FirestoreClient";
import { identify } from "./hooks/useInstrumentation";
import AppSkeleton from "./pages/AppSkeleton";
import DisabledAccountScreen from "./pages/Common/DisabledAccountScreen";
import ReactivationDialog from "./pages/Common/ReactivationDialog";
import UnderReviewScreen from "./pages/Common/UnderReviewScreen";
import { Partner } from "./types/Partner";
import { UserAccount, UserType } from "./types/User";
import isRecentInCustodyApplication from "./utils/isRecentInCustodyApplication";
import isRejectionRecent from "./utils/isRejectionRecent";
import isUserAccountDeactivated from "./utils/isUserAccountDeactivated";
import isUserOnboarded from "./utils/isUserOnboarded";
import { LIB_VERSION } from "./version";

export function useSessionAccountInformation(): SessionAccountInformation {
  const session = useSession();

  if (!session.metadata) throw new Error("Information was not loaded");

  return session.metadata;
}

export function useAdmin() {
  const session = useSession();

  if (!session.metadata)
    throw new Error("Only admins are allowed to access this");
  if (session.metadata.type !== UserType.Admin)
    throw new Error("Only admins are allowed to access this.");

  return session.metadata;
}

export function usePartner(): Partner {
  const session = useSession();
  if (!session.metadata) throw new Error("Type narrowing");
  if (session.metadata.type !== UserType.Partner)
    throw new Error("Type narrowing");

  return session.metadata;
}

/**
 * Returns the live user account from a single shared onSnapshot listener
 * in AuthContext. This eliminates duplicate Firestore listeners that
 * previously caused cascading re-renders and duplicate submissions.
 */
export function useCurrentUser(): UserAccount {
  const account = useSessionAccountInformation();
  const liveUser = useLiveUser();

  if (account.type !== UserType.User)
    throw new Error("This interface cannot be accessed by non-user accounts");

  // liveUser is populated by the single onSnapshot listener in AuthContext.
  // Fall back to session metadata if the listener hasn't fired yet.
  return liveUser ?? (account as UserAccount);
}

export type OnboardingRedirectRule =
  | "redirectToOnboardingIfRequired"
  | "redirectToDashboardIfOnboarded"
  | "allowAllAccountsAccess";

interface Props {
  children: JSX.Element;
  onboardingRedirectRule?: OnboardingRedirectRule;
  userType?: UserType;
}

const SessionBoundary = ({
  children,
  onboardingRedirectRule,
  userType,
}: Props): JSX.Element => {
  const authCtx = useAuth();
  const location = useLocation();
  const account = authCtx.session?.metadata;

  // Sync identity + app_version to both Amplitude and PostHog. Keyed on uid so
  // it only fires when the user actually changes (not on every re-render).
  // cohortId is included so PostHog feature flags can target by cohort
  // (e.g. release conditions on cohort-scoped experiments).
  const cohortId =
    account?.type === UserType.User ? account.cohortId : undefined;
  useEffect(() => {
    if (!account?.uid) return;
    identify({
      userId: account.uid,
      traits: {
        app_version: LIB_VERSION,
        ...(cohortId && { cohortId }),
      },
    });
  }, [account?.uid, cohortId]);

  if (authCtx.isLoading) {
    return <AppSkeleton />;
  }

  if (!authCtx.isLoggedIn) {
    // Redirect them to the /login page, but save the current location they were
    // trying to go to when they were redirected. This allows us to send them
    // along to that page after they login, which is a nicer user experience
    // than dropping them off on the home page.
    // Include both pathname and search parameters to preserve query strings
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  if (!account) {
    return <AppSkeleton />;
  }

  if (account.type === UserType.Partner) {
    return <Navigate to="/partners/dashboard" />;
  }

  if (account.type === UserType.Admin) {
    if (location.pathname.includes("admin")) {
      return children;
    }

    return <Navigate to="/admin" />;
  }

  // Redirection for routes that are exclusive to certain user types (e.g only students can access the student dashboard)
  if (userType && account.type !== userType) {
    return <Navigate to="/" />;
  }

  // User redirection
  if (account.type === UserType.User) {
    // Check if user was recently rejected (within 12 hours) or
    // in-custody user has recently completed application (within 24 hours)
    if (
      (isRejectionRecent(account) || isRecentInCustodyApplication(account)) &&
      onboardingRedirectRule !== "allowAllAccountsAccess"
    ) {
      return <UnderReviewScreen />;
    }

    // Inactivity is a reversible engagement state, so returning students may
    // continue to the page they requested. The prompt is an optional overlay
    // and remembers dismissal for this specific inactivity event. Other exit
    // states remain blocking below.
    if (
      account.applicationStatus === "inactive" &&
      onboardingRedirectRule !== "allowAllAccountsAccess"
    ) {
      const inactivityEvent =
        account.applicationStatusLastUpdatedAt ?? "legacy";
      const reactivationDismissalKey = `reactivation:${account.uid}:${inactivityEvent}`;

      return (
        <>
          {children}
          <ReactivationDialog
            key={reactivationDismissalKey}
            presentation="optional"
            dismissalKey={reactivationDismissalKey}
          />
        </>
      );
    }

    if (
      isUserAccountDeactivated(account) &&
      onboardingRedirectRule !== "allowAllAccountsAccess"
    )
      return <DisabledAccountScreen />;

    if (
      onboardingRedirectRule === "redirectToDashboardIfOnboarded" &&
      isUserOnboarded(account)
    ) {
      return <Navigate to="/" replace />;
    }

    // Redirect to success plan onboarding if user has an active account but not status
    // this means the application screen if the application is not complete
    // otherwise, if the application is complete, redirect to the dashboard
    if (
      onboardingRedirectRule === "redirectToOnboardingIfRequired" &&
      !isUserOnboarded(account)
    ) {
      if (account.application) {
        return <Navigate to="/onboarding/success-plan" replace />;
      }
      return <Navigate to="/onboarding" replace />;
    }
  }

  return children;
};

export default SessionBoundary;
